import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import './LandingPage.css';

// Google 폰트 로드
const loadGoogleFont = () => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap';
  document.head.appendChild(link);
};

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mainRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: mainRef,
    offset: ["start start", "end end"]
  });
  
  useEffect(() => {
    loadGoogleFont();
  }, []);
  
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  return (
    <div className="landing-page">
      {/* 네비게이션 바 */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/How2Play_mainLogo512.png" alt="How2Play Logo" />
            <span>How2Play</span>
          </div>
          
          <div className="nav-links">
            <a href="#playlist-match">플레이리스트 매칭</a>
            <a href="#club-map">클럽 맵</a>
            <a href="#vti">VTI 테스트</a>
            <Link to="/login" className="nav-button">로그인</Link>
          </div>
          
          <div className="mobile-menu-button" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <a href="#playlist-match" onClick={() => setIsMenuOpen(false)}>플레이리스트 매칭</a>
              <a href="#club-map" onClick={() => setIsMenuOpen(false)}>클럽 맵</a>
              <a href="#vti" onClick={() => setIsMenuOpen(false)}>VTI 테스트</a>
              <Link to="/login" onClick={() => setIsMenuOpen(false)}>로그인</Link>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      
      {/* 히어로 섹션 */}
      <motion.section 
        className="hero-section"
        ref={mainRef}
        style={{ opacity, scale }}
      >
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            당신에게 맞는 클럽을 <br />
            <span className="gradient-text">쉽게 찾으세요</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            음악 취향 기반 맞춤형 클럽 추천 서비스<br />
            How2Play에서 나에게 딱 맞는 클럽을 찾아보세요
          </motion.p>
          
          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            <Link to="/signup" className="primary-button">
              시작하기
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z" fill="currentColor"/>
              </svg>
            </Link>
            <a href="#vti" className="secondary-button">
              VTI 테스트 하기
            </a>
          </motion.div>
        </div>
        
        <motion.div 
          className="hero-image"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="mockup-container">
            <div className="phone-mockup">
              <div className="mockup-placeholder spotify-mockup"></div>
            </div>
          </div>
        </motion.div>
      </motion.section>
      
      {/* 플레이리스트 매칭 섹션 */}
      <section id="playlist-match" className="feature-section playlist-section">
        <motion.div 
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <h2>Spotify | Youtube Music <br /><span className="gradient-text">듣고 있는 플레이리스트 링크를 복사</span></h2>
          <p>당신의 음악 취향을 분석하여 최적의 클럽을 추천해 드립니다</p>
        </motion.div>
        
        <div className="feature-showcase">
          <motion.div 
            className="mockup-display"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="playlist-mockup feature-mockup"></div>
          </motion.div>
          
          <motion.div 
            className="feature-details"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">01</div>
              <h3>스트리밍 서비스 연동</h3>
              <p>Spotify, YouTube Music 등의 플레이리스트를 분석합니다</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">02</div>
              <h3>음악 취향 분석</h3>
              <p>당신이 즐겨듣는 음악의 패턴과 장르를 파악합니다</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">03</div>
              <h3>맞춤형 클럽 추천</h3>
              <p>분석 결과를 바탕으로 당신에게 딱 맞는 클럽을 추천해 드립니다</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* 클럽 맵 섹션 */}
      <section id="club-map" className="feature-section map-section">
        <motion.div 
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <h2>"<span className="gradient-text">아 어제 들었던 노래 나오는 클럽 어디 없나?</span>"</h2>
          <p>좋아하는 노래가 나오는 클럽 저희가 찾아드릴게요</p>
        </motion.div>
        
        <div className="feature-showcase reverse">
          <motion.div 
            className="feature-details"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">01</div>
              <h3>위치 기반 클럽 탐색</h3>
              <p>내 주변의 인기 클럽들을 한눈에 확인하세요</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">02</div>
              <h3>실시간 인기도 체크</h3>
              <p>지금 가장 핫한 클럽은 어디인지 확인할 수 있습니다</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">03</div>
              <h3>장르별 필터링</h3>
              <p>Hip-hop, House, Techno 등 원하는 장르의 클럽만 골라볼 수 있어요</p>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="mockup-display"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="map-mockup feature-mockup"></div>
          </motion.div>
        </div>
      </section>
      
      {/* 여기에 불러봐! 섹션 */}
      <section className="feature-section discover-section">
        <motion.div 
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <h2>여기에 <span className="gradient-text">불러봐!</span></h2>
          <p>마음에 드는 클럽을 더 깊게 탐색하세요</p>
        </motion.div>
        
        <div className="feature-showcase">
          <motion.div 
            className="mockup-display double-mockup"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="discover-mockup feature-mockup left"></div>
            <div className="detail-mockup feature-mockup right"></div>
          </motion.div>
          
          <motion.div 
            className="feature-details"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">01</div>
              <h3>세부 클럽 정보</h3>
              <p>입장료, 운영시간, 인기 장르 등 모든 정보를 한 곳에서 확인하세요</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">02</div>
              <h3>음악 취향 일치도</h3>
              <p>내 취향과 얼마나 일치하는지 퍼센트로 확인할 수 있어요</p>
            </motion.div>
            
            <motion.div className="feature-point" variants={fadeInUp}>
              <div className="point-number">03</div>
              <h3>리뷰 및 평가</h3>
              <p>실제 방문자들의 솔직한 리뷰로 분위기를 미리 파악하세요</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* VTI 테스트 섹션 */}
      <section id="vti" className="vti-section">
        <motion.div 
          className="section-title"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <h2>자주 방문하는 클럽을 모아보고<br /><span className="gradient-text">취향저격 클럽 큐레이션</span>을 받아보세요</h2>
          <p>당신만의 클럽 취향을 발견하고 새로운 경험을 만나보세요</p>
        </motion.div>
        
        <motion.div 
          className="feature-showcase reverse"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <div className="feature-details">
            <div className="vti-info">
              <h3>클럽 VTI 테스트</h3>
              <p>몇 가지 질문에 답하고 당신의 클럽 취향을 분석해보세요</p>
              <p>당신은 어떤 유형의 클러버인가요?</p>
              <Link to="/vti" className="primary-button">
                테스트 시작하기
              </Link>
            </div>
          </div>
          
          <div className="mockup-display double-mockup">
            <div className="club-list-mockup feature-mockup left"></div>
            <div className="profile-mockup feature-mockup right"></div>
          </div>
        </motion.div>
      </section>
      
      {/* 다운로드 섹션 */}
      <section className="download-section">
        <motion.div 
          className="download-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <div className="download-content">
            <h2>지금 바로 <span className="gradient-text">How2Play</span>를 만나보세요</h2>
            <p>앱 스토어와 구글 플레이에서 무료로 다운로드 가능합니다</p>
            <div className="store-buttons">
              <a href="https://apps.apple.com/kr/app/how2play/id6743025124" target="_blank" rel="noreferrer" className="store-button">
                <img src="/app-store.svg" alt="App Store" />
              </a>
              <a href="#" className="store-button coming-soon">
                <img src="/play-store.svg" alt="Play Store" />
                <span className="coming-soon-tag">Coming Soon</span>
              </a>
            </div>
          </div>
          <div className="download-image">
            <div className="screens-mockup-placeholder"></div>
          </div>
        </motion.div>
      </section>
      
      {/* 푸터 */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-logo">
            <img src="/How2Play_mainLogo512.png" alt="How2Play Logo" />
            <span>How2Play</span>
          </div>
          
          <div className="footer-links">
            <div className="footer-column">
              <h4>서비스</h4>
              <a href="#playlist-match">플레이리스트 매칭</a>
              <a href="#club-map">클럽 맵</a>
              <a href="#vti">VTI 테스트</a>
              <Link to="/login">로그인</Link>
            </div>
            
            <div className="footer-column">
              <h4>회사</h4>
              <a href="/about">회사 소개</a>
              <a href="mailto:contact@how2play.kr">제휴 문의</a>
              <a href="/careers">채용 정보</a>
            </div>
            
            <div className="footer-column">
              <h4>정책</h4>
              <Link to="/terms">이용약관</Link>
              <Link to="/privacy">개인정보처리방침</Link>
              <Link to="/marketing-consent">마케팅 정보 수신 동의</Link>
            </div>
            
            <div className="footer-column">
              <h4>문의</h4>
              <a href="mailto:contact@how2play.kr">이메일</a>
              <a href="https://instagram.com/how2play.app" target="_blank" rel="noreferrer">인스타그램</a>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>© 2023 장이신. All rights reserved.</p>
          <p>사업자등록번호: 454-41-01363 | 대표자: 신화랑</p>
          <p>주소: 서울특별시 성북구 길음로 119</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
